<script>
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import { openDialog } from "@/utils";
    import { mapGetters } from "vuex";
    import { cloneDeep, isEqual } from "lodash";
    import tabIcon from "@/assets/narvarostatistik.svg";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KHeader from "@/components/KHeader.vue";
    import KDateTimepicker from "@/components/forms/inputs/DateTimepicker/KDateTimepicker.vue";

    export default {
        extends: BaseTab,
        patientBound: true,
        name: "Spelare",
        tabName: "Spelare",
        icon: tabIcon,

        components: {
            KForm,
            KHeader,
            KDateTimepicker,
        },

        props: {
            tabId: Number,
            parameters: {
                type: Object,
                required: false,
            },
        },

        data() {
            return {
                defaultSpelare: { spelarelag: [] },
                spelare: { spelarelag: [] },
                spelareLoaded: false,
                spelareDefaultSet: false,
                inputErrors: {},
            };
        },

        computed: {
            ...mapGetters("patientData", ["patient"]),
            hasUnsavedData() {
                return !isEqual(this.defaultSpelare, this.spelare);
            },
        },

        methods: {
            getSpelareInitialValues() {
                return Promise.resolve(this.spelare);
            },
            onSpelareUpdated(formData) {
                this.spelare = { ...this.spelare, ...formData };
                if (!this.spelareDefaultSet) {
                    this.spelareDefaultSet = true;
                    this.defaultSpelare = cloneDeep(this.spelare);
                }
            },
            flattenErrors(data) {
                const errors = {};
                for (const [key, value] of Object.entries(data ?? {})) {
                    if (Array.isArray(value)) {
                        value.forEach((item, index) => {
                            for (const [subKey, subValue] of Object.entries(item ?? {})) {
                                errors[`${key}.${index}.${subKey}`] = subValue;
                            }
                        });
                    } else {
                        errors[key] = value;
                    }
                }
                return errors;
            },
            async submit(formData) {
                this.inputErrors = {};
                const toDate = (val) => (val ? String(val).slice(0, 10) : null);
                const spelarelag = (formData.spelarelag ?? []).map((item) => ({
                    ...item,
                    lag: item.lag?.value ?? item.lag,
                    aktiv_fran: toDate(item.aktiv_fran),
                    aktiv_till: toDate(item.aktiv_till),
                }));
                const data = { ...formData, spelarelag, id: this.spelare.id, patient: this.spelare.patient };
                try {
                    await this.$store.dispatch("skreg/spelare/saveSpelare", { spelare: data });
                    await this.$store.dispatch("tabs/closeTab", this.tabId);
                } catch (e) {
                    if (e.response?.status === 400) {
                        this.inputErrors = this.flattenErrors(e.response.data);
                    } else {
                        openDialog("Kunde inte spara spelaren.", "error");
                    }
                }
            },
            cancel() {
                this.closeTab(this.hasUnsavedData);
            },
        },

        async created() {
            try {
                const spelare = await this.$store.dispatch("skreg/spelare/fetchSpelare", {
                    patientId: this.patient.id,
                });
                if (spelare) {
                    const transformed = {
                        ...spelare,
                        spelarelag: (spelare.spelarelag ?? []).map((sl) => ({
                            ...sl,
                            lag: sl.lag_display ? { value: sl.lag, label: sl.lag_display.namn } : sl.lag,
                        })),
                    };
                    this.defaultSpelare = cloneDeep(transformed);
                    this.spelare = cloneDeep(transformed);
                }
            } catch (e) {
                // 404 = spelare finns inte än, formuläret laddas med standardvärden
            }
            this.spelareLoaded = true;
        },
    };
</script>

<template>
    <div class="spelare">
        <KForm
            v-if="spelareLoaded"
            id="spelare"
            :initialValuesGetter="getSpelareInitialValues"
            submitContent="Spara och stäng"
            :disableSubmit="!hasUnsavedData"
            cancelContent="Avbryt"
            :errors="inputErrors"
            @submit="submit"
            @updated="onSpelareUpdated"
            @cancel="cancel"
        >
            <template #header>
                <KHeader heading="Spelare" />
            </template>

            <KSection name="information" heading="Information" number="1">
                <KNumber name="langd" label="Längd" />

                <KNumber name="vikt" label="Vikt" validation="decimals:1" />

                <KText name="dominerande_fot" label="Dominerande fot" />

                <KText name="klubbfattning" label="Klubbfattning" />
            </KSection>

            <KSection name="lag" heading="Lag" number="2">
                <KRepeater name="spelarelag" add-label="Lägg till lag" :minimumOneRow="false">
                    <template #default="{ index, model }">
                        <LagAutocomplete
                            class="span-three"
                            label="Lag"
                            validation="required"
                            :value="model?.[index]?.lag ?? null"
                        />

                        <KNumber
                            class="span-two"
                            name="trojnummer"
                            label="Tröjnummer"
                            :value="model?.[index]?.trojnummer ?? null"
                        />

                        <KText class="span-two" name="position" label="Position" />

                        <KDateTimepicker
                            class="span-two"
                            name="aktiv_fran"
                            label="Från"
                            type="date"
                            validation="required"
                            :value="model?.[index]?.aktiv_fran ?? null"
                        />

                        <KDateTimepicker
                            class="span-two"
                            name="aktiv_till"
                            label="Till"
                            type="date"
                            :value="model?.[index]?.aktiv_till ?? null"
                        />
                    </template>
                </KRepeater>
            </KSection>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
